<template>
  <ModalModule
    v-if="software"
    v-model="activeModal"
    :title="
      $textDictionary['downloadCenterSoftware.modalTitle'] ??
      'downloadCenterSoftware.modalTitle'
    "
    close-icon-text="close"
    :esc-to-close="true"
    :click-to-close="false"
    :is-full-screen="true"
    @abort="emits('closeModal')"
    @after-close="emits('afterCloseModal')"
  >
    <div v-if="currentStep === 'overview'">
      <SoftwareCenterModalIntroBlock :software="props.software" />

      <template
        v-if="software?.supportedOs && !(software?.supportedOs.length <= 1)"
      >
        <p class="fw-bold mb-4 mt-4">
          {{
            $textDictionary['downloadCenterSoftware.operationSystemLabel'] ??
            'downloadCenterSoftware.operationSystemLabel'
          }}
        </p>
        <TabsComponent
          :id="software.id"
          tab-list-position="top"
          :tab-list-horizontal-width="300"
          :color-mode="'white'"
          @tab-change="onTabChange"
        >
          <TabPanel
            v-for="softwareOption in softwareOsOptions"
            :key="softwareOption.value"
            :uid="softwareOption.value"
            :title="softwareOption.label"
            :active-on-init="softwareOption.value === selectedOs"
            :icon="{
              name:
                softwareOption.value === 'macOs' ? 'mac' : softwareOption.value
            }"
          >
            <SoftwareCenterModalDownloadsTable
              :software-id="software.id"
              :software-links="softwareLinks"
              :further-software-links="furtherSoftwareLinks"
              @select-download="(asset) => selectDownload(asset)"
              @force-download-and-show-success="
                (downloadUrl, softwareName) =>
                  forceDownloadAndShowSuccess(downloadUrl, softwareName)
              "
            />
          </TabPanel>
        </TabsComponent>
      </template>
      <template v-else>
        <SpacerBlock
          v-if="!software?.supportedOs || software?.supportedOs.length <= 1"
          spacing-grid="xsmall"
        />

        <SoftwareCenterModalDownloadsTable
          :software-id="software.id"
          :software-links="softwareLinks"
          :further-software-links="furtherSoftwareLinks"
          @select-download="(asset) => selectDownload(asset)"
          @force-download-and-show-success="
            (downloadUrl, softwareName) =>
              forceDownloadAndShowSuccess(downloadUrl, softwareName)
          "
        />
      </template>
    </div>

    <div v-else-if="currentStep === 'form' && formUrl">
      <SoftwareCenterModalIntroBlock :software="props.software" />
      <div class="row mt-5">
        <div class="col-12">
          <PardotFormIframe
            :id="props.software?.id"
            :form-url="formUrl"
            @success="handleFormSuccess"
          />
          <div class="d-flex justify-content-center col-lg-9 col-sm-12">
            <Button
              style-type="secondary"
              class="mb-4 align-self-center"
              @click="handeBackToOverview"
            >
              {{
                $textDictionary[
                  'downloadCenter.modal.success.back_button.label'
                ] ?? 'Back to overview'
              }}
            </Button>
          </div>
        </div>
      </div>
    </div>

    <DownloadCenterModalSuccessBlock
      v-else-if="currentStep === 'success'"
      :download-link="selectedDownloadLink"
      :hande-back-to-overview="handeBackToOverview"
    />
  </ModalModule>
  <ModalModule
    v-else
    v-model="activeModal"
    title="Download Software"
    close-icon-text="close"
    :esc-to-close="true"
    :click-to-close="false"
    @abort="emits('closeModal')"
    @after-close="emits('afterCloseModal')"
  >
    <LibRichTextRenderer :rich-text="noDownloadFoundHint" />
  </ModalModule>
</template>

<script setup lang="ts">
import {
  Button,
  ModalModule,
  SpacerBlock,
  TabPanel,
  TabsComponent
} from '@hypercodestudio/basler-components';
import PardotFormIframe from '~/components/downloads/PardotFormIframe.vue';
import type { SoftwareEntryModel } from '~~/lib/DownloadService/model';
import { forceDownload } from '~/utils/forceDownload';
import type {
  HyperlinkInterface,
  TabData
} from '@hypercodestudio/basler-components';

import DownloadCenterModalSuccessBlock from '~/components/downloads/DownloadCenterModalSuccessBlock.vue';
import SoftwareCenterModalIntroBlock from '~/components/downloads/software/SoftwareCenterModalIntroBlock.vue';
import SoftwareCenterModalDownloadsTable from '~/components/downloads/software/SoftwareCenterModalDownloadsTable.vue';
import { useSoftwareOsLabel } from '~~/lib/DownloadService/helper/useSoftwareOsLabel';
import { ref } from 'vue';

const {
  $textDictionary,
  $dictionary,
  $globalPageSettings,
  $currentEnvName,
  $analytics
} = useNuxtApp();
const logger = useLogger();
const url = useRequestURL();

interface Props {
  software?: SoftwareEntryModel | null;
  open?: boolean;
  preferredOs?: string;
}

const props = defineProps<Props>();

const emits = defineEmits<{
  /**
   * Once the modal was close via the "close" button (is not triggered via "esc").
   */
  closeModal: [];
  /**
   * After the modal was closed (emits always, also while closing via "esc").
   */
  afterCloseModal: [];
}>();

type Steps = 'overview' | 'form' | 'success';

const currentStep = ref<Steps>('overview');

const noDownloadFoundHint = $dictionary.value['downloadCenter.modal.notFound'];

const activeModal = ref(props.open);

function _findFurtherDownloads(
  supportedOs?: ReadonlyArray<string>
): FurtherDownloads[] {
  const filteredDownloads = props.software?.groupAssets?.filter((download) => {
    if (
      download.supportedOs?.includes(supportedOs?.[0] ?? '') &&
      !download.isMainAsset
    ) {
      return download;
    }
  });
  const furtherDownloads: FurtherDownloads[] = [];
  if (filteredDownloads) {
    for (const download of filteredDownloads) {
      if (!download.mediaLink) {
        logger.warn(
          'software further download has no media link - skipped',
          download
        );

        continue;
      }

      furtherDownloads.push({
        name: download.name,
        uri: download.mediaLink
      });
    }
  }

  return furtherDownloads;
}

watch(
  () => props.open,
  () => {
    activeModal.value = props.open;

    if (!props.open) {
      // reset download state on close
      currentStep.value = 'overview';
    }
  }
);

const { isMacOS, isAndroid, isWindows, userAgent } = useDevice();

const userOs = computed<string | undefined>(() => {
  if (isWindows) {
    return 'windows';
  }
  if (isMacOS) {
    return 'macOs';
  }
  if (isAndroid) {
    return 'android';
  }
  if (userAgent.toLowerCase().includes('linux')) {
    return 'linux';
  }
  return undefined;
});

const softwareOsOptions = computed(() => {
  const items = [];

  if (props.software?.supportedOs) {
    for (const label of Object.values(props.software?.supportedOs)) {
      items.push({
        value: label,
        label: useSoftwareOsLabel(label)
      });
    }
  }
  return items.sort(function (a, b) {
    if (a.value < b.value) {
      return -1;
    }
    if (a.value > b.value) {
      return 1;
    }
    return 0;
  });
});

const initialSelectedOs = computed(() => {
  if (
    props.preferredOs &&
    props.software?.supportedOs?.includes(props.preferredOs)
  ) {
    return props.preferredOs;
  }
  if (userOs.value && props.software?.supportedOs?.includes(userOs.value)) {
    return userOs.value;
  }

  return props.software?.supportedOs?.[0];
});

const selectedOs = ref(initialSelectedOs.value);

const onTabChange = (tab: TabData) => {
  selectedOs.value = tab.computedId;
};

watch(initialSelectedOs, () => {
  if (initialSelectedOs.value) {
    selectedOs.value = initialSelectedOs.value;
  }
});

type SoftwareLinks = {
  formRequired: boolean;
  os: {
    icon?: string;
    name: string;
    nameEN: string;
    uri?: string;
  };
  format: {
    icon: 'download';
    fileExtension?: string;
    readableFilesize?: string;
    uri: string;
  };
  platform?: string[];
};

type FurtherDownloads = {
  name?: string;
  nameEN?: string;
  uri: string;
};

const softwareLinks = computed<SoftwareLinks[]>(() => {
  const groupedAssets: SoftwareLinks[] = [];
  if (!props.software) {
    return [];
  }

  for (const entry of props.software.groupAssets!) {
    const matchesOs =
      !entry.supportedOs ||
      (selectedOs.value && entry.supportedOs.includes(selectedOs.value));

    if (!entry.isMainAsset || !matchesOs) {
      continue;
    }

    // every main software entry must have a link to actually work
    if (!entry.mediaLink) {
      logger.warn('software main asset has no media link - skipped', entry);

      continue;
    }

    groupedAssets.push({
      formRequired: entry.formRequired ?? false,
      os: {
        icon: entry.supportedOs?.[0],
        name: entry.name ?? 'File',
        nameEN: entry.nameEN ?? 'File EN',
        uri: entry.mediaLink
      },
      format: {
        icon: 'download',
        fileExtension: entry.fileExtension,
        readableFilesize: entry.readableFilesize,
        uri: entry.mediaLink
      },
      platform: entry.platform
    });
  }
  return groupedAssets;
});

const furtherSoftwareLinks = computed<FurtherDownloads[]>(() => {
  const groupedAssets: FurtherDownloads[] = [];
  if (!props.software) {
    return [];
  }

  for (const entry of props.software.groupAssets!) {
    const matchesOs =
      !entry.supportedOs ||
      (selectedOs.value && entry.supportedOs.includes(selectedOs.value));

    if (entry.isMainAsset || !matchesOs) {
      continue;
    }

    if (!entry.mediaLink) {
      logger.warn('software asset has no media link - skipped', entry);

      continue;
    }

    groupedAssets.push({
      name: entry.name ?? 'File',
      nameEN: entry.nameEN ?? 'File EN',
      uri: entry.mediaLink
    });
  }
  return groupedAssets;
});

// e.g. 'https://info.baslerweb.com/l/73192/2020-02-24/98pcbf';
const formUrl = computed(() => {
  const urlString = props.software?.downloadGroupFormUrl
    ? props.software?.downloadGroupFormUrl
    : props.software?.downloadCategoryFormUrl
      ? props.software?.downloadCategoryFormUrl
      : $globalPageSettings.value?.softwarePardotFormUrl;

  if (!urlString) {
    return undefined;
  }

  try {
    const formUrl = new URL(urlString);
    formUrl.searchParams.append('download', 'software');
    formUrl.searchParams.append('domain', $currentEnvName.value);

    const filename = props.software?.name;
    if (filename) {
      formUrl.searchParams.append('filename', filename);
    }

    return formUrl.toString();
  } catch (e) {
    logger.error('could not create new url from pardot url', e);
  }
  return undefined;
});

const selectedDownloadUri = ref<string | undefined>();
const selectedDownloadNameEN = ref<string | undefined>();
const selectedDownloadLink = computed<HyperlinkInterface>(() => {
  return {
    uri: selectedDownloadUri.value ?? '#',
    target: '_self',
    external: false
  } satisfies HyperlinkInterface;
});

function forceDownloadAndShowSuccess(
  downloadUrl: string,
  softwareName?: string
): void {
  currentStep.value = 'success';
  forceDownload(downloadUrl);
  trackDownload(softwareName);
}

function selectDownload(download: SoftwareLinks): void {
  selectedDownloadUri.value = download.format.uri;
  selectedDownloadNameEN.value = download.os.nameEN;

  if (download.formRequired) {
    currentStep.value = 'form';

    return;
  }

  forceDownloadAndShowSuccess(
    selectedDownloadUri.value,
    selectedDownloadNameEN.value
  );
}

function handleFormSuccess(recipientEmail?: string): void {
  currentStep.value = 'success';
  $analytics?.pushToDataLayer({
    event: 'formSent',
    goalPage: '/downloadform/sent',
    page_location: url.href,
    // Enhanced Conversions: provide the recipient email so the download
    // conversion can be attributed even without cookies / across devices.
    ...(recipientEmail ? { recipientEmail } : {})
  });

  if (selectedDownloadUri.value) {
    forceDownload(selectedDownloadUri.value);
  }
  trackDownload(selectedDownloadNameEN.value);
}

function trackDownload(action?: string) {
  $analytics.pushToDataLayer({
    action: action ?? 'no_label_found',
    category: 'download_successful',
    event: 'file_download_c',
    label: props.software?.id ?? 'no_id_found'
  });
}

function handeBackToOverview(): void {
  currentStep.value = 'overview';
}
</script>
